import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  Input,
  ConfirmationModal,
  Page,
  Select,
  SelectItem,
  Skeleton,
  Stack,
  Switch,
  Text,
  Title,
  useToasts,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useTableById,
  useTableCheckSlug,
  useTableSave,
  useTabsByEntityType,
} from "@/queries/use-custom-fields.query.ts";
import { useEffect, useState } from "react";
import { ApiCFEntityTypes, ApiCFTabType, ApiTableTypes } from "@/types/custom-field.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { useParams } from "react-router-dom";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import NotFound from "@/pages/NotFound.tsx";
import { EmployeeTableCustomFieldList } from "@/pages/settings/custom-fields/components/EmployeeTableCustomFieldList.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";

export function EmployeeCustomTable() {
  const { t } = useTranslation();

  useTitle(t("custom_fields:settings_title"), t("common:settings"));

  const { showInfo } = useToasts();
  const { openModal } = useModals();
  const { getTabTitle } = useCustomFieldsHook();
  const { isFeatureEnabled } = usePermissions();
  const customFieldsEnabled = isFeatureEnabled(ApiFeatureType.custom_fields);
  const { table_id } = useParams();
  const fieldId = table_id ? Number(table_id) : undefined;
  const { data: tabs } = useTabsByEntityType(ApiCFEntityTypes.employee);
  const tabOptions =
    tabs
      ?.filter((tab) => tab.type !== ApiCFTabType.sidebar)
      .map(
        (tab) =>
          ({
            id: tab.type,
            title: getTabTitle(tab),
          }) as SelectItem
      ) ?? [];
  const { table, isLoading } = useTableById(fieldId!);
  const isSystemTable = table?.table_type !== ApiTableTypes.custom;
  const canEditTableSettings = customFieldsEnabled && !isSystemTable;
  const tableTabType = tabs?.find((tab) => tab.id === table?.tab_id)?.type;
  const [checkedTitle, setCheckedTitle] = useState<string>("");

  const EmployeeCustomTableSchemaObject = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    is_historical: z.boolean().optional(),
    tab_type: z.enum(ApiCFTabType, { error: t("errors:field_required") }),
  });

  const {
    handleSubmit,
    register,
    control,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(EmployeeCustomTableSchemaObject),
    defaultValues: {
      title: table?.title ?? "",
      tab_type: tableTabType,
      is_historical: table?.is_historical ?? false,
    },
  });
  useEffect(() => {
    if (table) {
      reset({
        title: table.title,
        tab_type: tableTabType,
        is_historical: table.is_historical ?? false,
      });
    }
  }, [table, tableTabType, reset]);
  const { mutateAsync: save, isPending: isSavePending } = useTableSave(control);
  const { data: checkResult } = useTableCheckSlug(checkedTitle, table?.id, canEditTableSettings);
  const slugIsOk = checkedTitle === table?.title || (checkResult?.success ?? true);
  const fieldTitle = watch("title");

  const saveTable = async (data: {
    title: string;
    tab_type: ApiCFTabType;
    is_historical?: boolean;
  }) => {
    if (isSavePending || !slugIsOk) return;

    const fieldSaved = await save({
      id: table?.id,
      payload: {
        title: data.title,
        tab_type: data.tab_type,
        is_historical: data.is_historical ?? false,
      },
    });

    if (fieldSaved) {
      analyticsService.trackEvent(analyticEvents.customFields.fieldCreated, {
        [analyticProperties.id]: fieldSaved.id,
      });

      showInfo({ title: t("common:you_changes_were_saved") });
    }
  };

  const onSubmit = handleSubmit(saveTable);

  useDebounceUpdate({
    data: fieldTitle,
    timeout: 300,
    execute: setCheckedTitle,
  });

  if (isLoading) {
    return (
      <Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
      </Stack>
    );
  }

  if (!table) {
    return <NotFound />;
  }

  return (
    <Page>
      <Page.Header
        title={t(
          isSystemTable
            ? "custom_fields:system_table_settings"
            : "custom_fields:custom_table_settings"
        )}
        showBack={true}
      ></Page.Header>
      <Page.Content>
        <Stack className="px-16">
          <Title header={t("common:details")} paddingTop paddingBottom size="lg" />
          <form onSubmit={onSubmit}>
            <Stack gap={"md"}>
              <Stack>
                <Input
                  {...register("title")}
                  label={t("common:title")}
                  error={errors.title?.message}
                  onBlur={() => {
                    if (canEditTableSettings && fieldTitle !== table.title) {
                      onSubmit().then();
                    }
                  }}
                  disabled={!canEditTableSettings}
                  required
                />
                <Controller
                  render={({ field: { onChange, value } }) => (
                    <Select
                      emptyTitle={t("common:section")}
                      onChange={(e) => {
                        onChange(e);
                        if (canEditTableSettings) onSubmit().then();
                      }}
                      value={value}
                      options={tabOptions}
                      required
                      error={errors.tab_type?.message}
                      disabled={!canEditTableSettings}
                    />
                  )}
                  name={"tab_type"}
                  control={control}
                />
              </Stack>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Switch
                    onChange={(isHistorical) => {
                      const saveHistoricalValue = async () => {
                        onChange(isHistorical);
                        await saveTable({
                          ...getValues(),
                          is_historical: isHistorical,
                        });
                      };

                      if (value && !isHistorical) {
                        openModal(ConfirmationModal, {
                          question: t("custom_fields:disable_historical_confirmation_title"),
                          text: t("custom_fields:disable_historical_confirmation_description"),
                          acceptTitle: t("common:continue"),
                          onAccept: saveHistoricalValue,
                        });
                        return;
                      }

                      void saveHistoricalValue();
                    }}
                    checked={value}
                    disabled={!canEditTableSettings}
                    title={t("custom_fields:historical_title")}
                    description={t("custom_fields:historical_description")}
                  />
                )}
                name={"is_historical"}
                control={control}
              />
              <Stack gap={"md"}>
                {fieldTitle?.length > 0 && !slugIsOk ? (
                  <Stack className={"h-14"} justify={"center"}>
                    <Text className={"text-danger"}>{t("custom_fields:change_table_name")}</Text>
                  </Stack>
                ) : (
                  <></>
                )}
                <ServerErrorField errors={errors} />

                {/*<Button*/}
                {/*  size={"lg"}*/}
                {/*  variant={"primary"}*/}
                {/*  onClick={onSubmit}*/}
                {/*  isLoading={isSavePending}*/}
                {/*>*/}
                {/*  {t("common:save")}*/}
                {/*</Button>*/}
              </Stack>
            </Stack>
          </form>
          <EmployeeTableCustomFieldList table={table} />
        </Stack>
      </Page.Content>
    </Page>
  );
}
